<template>
  <div class="auth-page-container">
    <div class="ambient-glow ambient-glow--indigo"></div>

    <v-row align="center" justify="center" class="fill-height ma-0 auth-content-row">
      <v-col cols="12" sm="10" md="8" lg="5" xl="4">
        <v-card class="glass-card pa-6 pa-sm-8 auth-card" elevation="0">
          <div class="text-center mb-8">
            <div class="auth-brand-badge mb-3">
              <v-icon size="32" color="primary">mdi-shield-key-outline</v-icon>
            </div>
            <h1 class="text-h4 font-weight-bold mb-2">Create New Password</h1>
            <p class="text-body-2 text-medium-emphasis">
              Set a strong, new password for your account
            </p>
          </div>

          <v-form @submit.prevent="resetPassword">
            <v-text-field
              v-model="email"
              label="Account Email"
              type="email"
              prepend-inner-icon="mdi-email-outline"
              density="comfortable"
              variant="outlined"
              readonly
              class="mb-3"
            />

            <v-text-field
              v-model="token"
              label="Reset Token"
              prepend-inner-icon="mdi-key-outline"
              density="comfortable"
              variant="outlined"
              readonly
              class="mb-3"
            />

            <v-text-field
              v-model="newPassword"
              label="New Password"
              placeholder="Min 8 chars (uppercase, lowercase, digit, special)"
              type="password"
              prepend-inner-icon="mdi-lock-outline"
              density="comfortable"
              variant="outlined"
              required
              class="mb-4"
            />

            <v-btn
              type="submit"
              color="primary"
              size="large"
              block
              class="btn-modern font-weight-bold text-white mb-2"
              :loading="loading"
              prepend-icon="mdi-lock-check"
            >
              Reset Password
            </v-btn>
          </v-form>

          <v-divider class="my-6" />

          <div class="text-center">
            <v-btn
              to="/login"
              variant="text"
              class="btn-modern"
              prepend-icon="mdi-arrow-left"
            >
              Back to Sign In
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="6000"
      location="top"
      rounded="lg"
    >
      {{ snackbar.text }}
      <template #actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api2 from '@/services/api2'

export default {
  name: 'ResetPassword',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const email = ref('')
    const token = ref('')
    const newPassword = ref('')
    const loading = ref(false)
    const snackbar = ref({
      show: false,
      text: '',
      color: 'success',
    })

    onMounted(() => {
      email.value = route.query.email || ''
      token.value = route.query.token || ''
    })

    const resetPassword = async () => {
      if (!newPassword.value) return
      loading.value = true
      try {
        await api2.put('/api/auth/reset-password', {
          email: email.value,
          token: token.value,
          password: newPassword.value,
        })
        snackbar.value = {
          show: true,
          text: 'Password reset successfully. Please login with your new password.',
          color: 'success',
        }
        setTimeout(() => {
          router.push('/login')
        }, 5000)
      } catch (error) {
        console.error('Password reset failed:', error)
        snackbar.value = {
          show: true,
          text: 'Password reset failed. Please try again or request a new reset link.',
          color: 'error',
        }
      } finally {
        loading.value = false
      }
    }

    return {
      email,
      token,
      newPassword,
      loading,
      resetPassword,
      snackbar,
    }
  },
}
</script>

<style scoped>
.auth-page-container {
  min-height: calc(100vh - 72px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 32px 16px;
}

.auth-content-row {
  position: relative;
  z-index: 2;
  width: 100%;
}

.auth-card {
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25) !important;
}

.auth-brand-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
}

.ambient-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.25;
}

.ambient-glow--indigo {
  width: 450px;
  height: 450px;
  top: 10%;
  left: 20%;
  background: #6366f1;
}
</style>